import { useEffect, useMemo, useState } from 'react';
import { createOrderDraft, ensureOrderDraftV02, getOrderDraftFilename, updateOrderDraftFromForm } from '../lib/createOrderDraft';
import { saveOrderDraft } from '../lib/orderStorage';
import { loadLastProject } from '../lib/projectStorage';
import type { CalendarProject } from '../types/calendarProject';
import type { OrderDraft } from '../types/orderDraft';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const progressSteps = ['Produkt', 'Fotky', 'Návrh', 'Editor', 'Preflight', 'Objednávka'];
const preflightLabels = { not_started: 'Nezkontrolováno', ok: 'OK', warning: 'Upozornění', error: 'Chyby' };
const deliveryOptions = [
  { id: 'pickup', title: 'Osobní odběr', description: 'Vyberete si termín vyzvednutí po dokončení výroby.' },
  { id: 'courier', title: 'Kurýr', description: 'Doručení na adresu. Cena je zatím placeholder.' },
  { id: 'packet', title: 'Výdejní místo', description: 'Zásilkovna / výdejní místo bude napojeno později.' },
] as const;
const billingOptions = [
  { id: 'personal', title: 'Soukromá osoba', description: 'Jednoduchá fakturace pro osobní objednávku.' },
  { id: 'company', title: 'Firma', description: 'Fakturační údaje s názvem firmy a IČO.' },
] as const;

type ValidationErrors = Record<string, string>;
type TouchedFields = Record<string, boolean>;

function downloadJson(fileName: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function sanitizeProjectForExport(project: CalendarProject) {
  return {
    ...project,
    photos: project.photos.map(({ objectUrl: _objectUrl, ...photo }) => photo),
  };
}

function validateDraft(draft: OrderDraft): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!draft.customer.fullName.trim()) errors.fullName = 'Vyplň jméno a příjmení.';
  if (!draft.customer.email.trim()) errors.email = 'Vyplň e-mail.';
  else if (!draft.customer.email.includes('@') || !draft.customer.email.includes('.')) errors.email = 'Zadej platný e-mail.';

  if (draft.delivery.method === 'courier' || draft.delivery.method === 'packet') {
    if (!draft.delivery.street.trim()) errors.deliveryStreet = 'Vyplň ulici pro doručení.';
    if (!draft.delivery.city.trim()) errors.deliveryCity = 'Vyplň město pro doručení.';
    if (!draft.delivery.postalCode.trim()) errors.deliveryPostalCode = 'Vyplň PSČ pro doručení.';
    if (!draft.delivery.country.trim()) errors.deliveryCountry = 'Vyplň zemi doručení.';
  }

  if (draft.billing.type === 'company') {
    if (!draft.billing.companyName.trim()) errors.billingCompanyName = 'Vyplň název firmy.';
    if (!draft.billing.ico.trim()) errors.billingIco = 'Vyplň IČO.';
  }

  if (!draft.billing.sameAsDelivery) {
    if (!draft.billing.street.trim()) errors.billingStreet = 'Vyplň fakturační ulici.';
    if (!draft.billing.city.trim()) errors.billingCity = 'Vyplň fakturační město.';
    if (!draft.billing.postalCode.trim()) errors.billingPostalCode = 'Vyplň fakturační PSČ.';
    if (!draft.billing.country.trim()) errors.billingCountry = 'Vyplň zemi fakturace.';
  }

  if (!draft.consent.termsAccepted) errors.termsAccepted = 'Souhlas s obchodními podmínkami je povinný.';
  if (!draft.consent.printApprovalAccepted) errors.printApprovalAccepted = 'Schválení návrhu je povinné.';
  if (!draft.consent.localAssetNoticeAccepted) errors.localAssetNoticeAccepted = 'Potvrď informaci o lokálním uložení fotek.';
  return errors;
}

function getPrimaryDraftStatus(draft: OrderDraft) {
  return draft.validationSummary?.isValid ? 'Draft připraven' : 'Chybí údaje';
}

function FieldError({ message, visible }: { message?: string; visible?: boolean }) {
  return message && visible ? <small className="order-field-error">{message}</small> : null;
}

function getMissingCount(draft: OrderDraft) {
  return (draft.validationSummary?.missingFields.length ?? 0) + (draft.validationSummary?.requiredConsentsMissing.length ?? 0);
}

export function OrderFlow() {
  const [project, setProject] = useState<CalendarProject | null>(null);
  const [draft, setDraft] = useState<OrderDraft | null>(null);
  const [touched, setTouched] = useState<TouchedFields>({});
  const [attemptedDownload, setAttemptedDownload] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const loadedProject = loadLastProject();
    setProject(loadedProject);
    if (loadedProject) {
      const nextDraft = createOrderDraft(loadedProject);
      setDraft(nextDraft);
      saveOrderDraft(nextDraft);
    }
  }, []);

  const errors = useMemo(() => (draft ? validateDraft(draft) : {}), [draft]);
  const primaryStatus = draft ? getPrimaryDraftStatus(draft) : 'Chybí údaje';
  const missingCount = draft ? getMissingCount(draft) : 0;
  const shouldShowError = (field: string) => Boolean(touched[field] || attemptedDownload);
  const touch = (field: string) => setTouched((current) => ({ ...current, [field]: true }));

  const patchDraft = (formData: Parameters<typeof updateOrderDraftFromForm>[1]) => {
    if (!draft) return;
    const nextDraft = updateOrderDraftFromForm(draft, formData);
    setDraft(nextDraft);
    saveOrderDraft(nextDraft);
  };

  const downloadOrderDraft = () => {
    if (!draft) return;
    setAttemptedDownload(true);
    const exportDraft = ensureOrderDraftV02(draft);
    setDraft(exportDraft);
    saveOrderDraft(exportDraft);
    if (!exportDraft.validationSummary?.isValid) {
      setNotice('Draft není kompletní. JSON stáhneš i tak, ale obsahuje validationSummary s chybějícími údaji.');
    } else {
      setNotice('Order draft je připravený jako lokální metadata export.');
    }
    downloadJson(getOrderDraftFilename(exportDraft), exportDraft);
  };

  const downloadProjectJson = () => {
    if (!project || !draft) return;
    downloadJson(draft.files.projectJsonFilename, sanitizeProjectForExport(project));
  };

  if (!project || !draft) {
    return (
      <main className="order-shell">
        <section className="order-empty-state">
          <Logo />
          <h1>Objednávka není připravená</h1>
          <p>Nejdřív otevři rozpracovaný projekt nebo vytvoř nový kalendář.</p>
          <div>
            <Button href="/projects">Moje projekty</Button>
            <Button href="/start" variant="secondary">Vytvořit kalendář</Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="order-shell">
      <div className="order-card">
        <header className="order-header">
          <Logo />
          <div className="order-header__project">
            <strong>{draft.projectSummary.title}</strong>
            <span>{primaryStatus} · Produkční odeslání vypnuto</span>
          </div>
          <nav>
            <Button href="/preflight" variant="secondary">Zpět na preflight</Button>
            <Button href="/projects" variant="secondary">Moje projekty</Button>
          </nav>
        </header>

        <nav className="order-progress" aria-label="Kroky Magic Flow">
          {progressSteps.map((step, index) => <span key={step} className={index === 5 ? 'is-active' : ''}><small>{String(index + 1).padStart(2, '0')}</small>{step}</span>)}
        </nav>

        <section className="order-hero">
          <p className="eyebrow">ORDER DRAFT V0.2</p>
          <h1>Objednávka</h1>
          <p>Připravíme objednávkový draft. V této MVP verzi se nic neodesílá a neplatí.</p>
          <div className="order-status-panel">
            <span className={draft.validationSummary?.isValid ? 'is-ready' : 'is-missing'}>{primaryStatus}</span>
            {draft.production.hasErrors && <span className="is-warning">Preflight obsahuje chyby</span>}
            <span className="is-disabled">Produkční odeslání vypnuto</span>
            <p>{draft.validationSummary?.isValid ? 'Všechny povinné údaje pro lokální draft jsou vyplněné.' : `Chybí ${missingCount} povinných položek nebo souhlasů.`}</p>
          </div>
          {notice && <strong className="order-warning">{notice}</strong>}
        </section>

        <div className="order-layout">
          <div className="order-main">
            <section className="order-section order-project-summary">
              <h2>Shrnutí projektu</h2>
              <dl className="order-summary-grid">
                <div><dt>Produkt</dt><dd>{draft.projectSummary.productName}</dd></div>
                <div><dt>Období</dt><dd>{draft.projectSummary.startMonth}/{draft.projectSummary.periodYear}</dd></div>
                <div><dt>Počet stran</dt><dd>{draft.projectSummary.pageCount}</dd></div>
                <div><dt>Fotky</dt><dd>{draft.projectSummary.photoCount}</dd></div>
                <div><dt>Preflight</dt><dd>{preflightLabels[draft.production.preflightStatus]}</dd></div>
                <div><dt>Assety</dt><dd>Lokálně {draft.production.localAssetCount} · cloud {draft.production.cloudAssetCount}</dd></div>
              </dl>
              <p>Fotky jsou v této MVP verzi uložené pouze lokálně v tomto prohlížeči.</p>
            </section>

            <section className="order-section">
              <h2>Kontaktní údaje</h2>
              <div className="order-form-grid">
                <label>Jméno a příjmení<input placeholder="Např. Jana Nováková" value={draft.customer.fullName} onBlur={() => touch('fullName')} onChange={(event) => patchDraft({ customer: { fullName: event.target.value } })} /><FieldError message={errors.fullName} visible={shouldShowError('fullName')} /></label>
                <label>E-mail<input type="email" placeholder="jana@example.cz" value={draft.customer.email} onBlur={() => touch('email')} onChange={(event) => patchDraft({ customer: { email: event.target.value } })} /><FieldError message={errors.email} visible={shouldShowError('email')} /></label>
                <label>Telefon<input placeholder="+420 777 123 456" value={draft.customer.phone} onChange={(event) => patchDraft({ customer: { phone: event.target.value } })} /></label>
              </div>
            </section>

            <section className="order-section">
              <h2>Doručení</h2>
              <div className="order-option-grid">
                {deliveryOptions.map((option) => (
                  <label key={option.id} className={draft.delivery.method === option.id ? 'order-option-card is-selected' : 'order-option-card'}>
                    <input type="radio" checked={draft.delivery.method === option.id} onChange={() => patchDraft({ delivery: { method: option.id } })} />
                    <strong>{option.title}</strong>
                    <span>{option.description}</span>
                  </label>
                ))}
              </div>
              {(draft.delivery.method === 'courier' || draft.delivery.method === 'packet') && (
                <div className="order-form-grid">
                  <label>Ulice<input placeholder="Ulice a číslo popisné" value={draft.delivery.street} onBlur={() => touch('deliveryStreet')} onChange={(event) => patchDraft({ delivery: { street: event.target.value } })} /><FieldError message={errors.deliveryStreet} visible={shouldShowError('deliveryStreet')} /></label>
                  <label>Město<input placeholder="Praha" value={draft.delivery.city} onBlur={() => touch('deliveryCity')} onChange={(event) => patchDraft({ delivery: { city: event.target.value } })} /><FieldError message={errors.deliveryCity} visible={shouldShowError('deliveryCity')} /></label>
                  <label>PSČ<input placeholder="110 00" value={draft.delivery.postalCode} onBlur={() => touch('deliveryPostalCode')} onChange={(event) => patchDraft({ delivery: { postalCode: event.target.value } })} /><FieldError message={errors.deliveryPostalCode} visible={shouldShowError('deliveryPostalCode')} /></label>
                  <label>Země<input placeholder="CZ" value={draft.delivery.country} onBlur={() => touch('deliveryCountry')} onChange={(event) => patchDraft({ delivery: { country: event.target.value } })} /><FieldError message={errors.deliveryCountry} visible={shouldShowError('deliveryCountry')} /></label>
                  <label className="is-wide">Poznámka<textarea placeholder="Upřesnění doručení nebo výdejního místa" value={draft.delivery.note} onChange={(event) => patchDraft({ delivery: { note: event.target.value } })} /></label>
                </div>
              )}
            </section>

            <section className="order-section">
              <h2>Fakturace</h2>
              <div className="order-option-grid order-option-grid--billing">
                {billingOptions.map((option) => (
                  <label key={option.id} className={draft.billing.type === option.id ? 'order-option-card is-selected' : 'order-option-card'}>
                    <input type="radio" checked={draft.billing.type === option.id} onChange={() => patchDraft({ billing: { type: option.id } })} />
                    <strong>{option.title}</strong>
                    <span>{option.description}</span>
                  </label>
                ))}
              </div>
              {draft.billing.type === 'company' && (
                <div className="order-form-grid">
                  <label>Název firmy<input placeholder="Název firmy s.r.o." value={draft.billing.companyName} onBlur={() => touch('billingCompanyName')} onChange={(event) => patchDraft({ billing: { companyName: event.target.value } })} /><FieldError message={errors.billingCompanyName} visible={shouldShowError('billingCompanyName')} /></label>
                  <label>IČO<input placeholder="12345678" value={draft.billing.ico} onBlur={() => touch('billingIco')} onChange={(event) => patchDraft({ billing: { ico: event.target.value } })} /><FieldError message={errors.billingIco} visible={shouldShowError('billingIco')} /></label>
                  <label>DIČ<input placeholder="CZ12345678" value={draft.billing.dic} onChange={(event) => patchDraft({ billing: { dic: event.target.value } })} /></label>
                </div>
              )}
              <label className="order-inline-check"><input type="checkbox" checked={draft.billing.sameAsDelivery} onChange={(event) => patchDraft({ billing: { sameAsDelivery: event.target.checked } })} /> Stejná jako doručovací</label>
              {!draft.billing.sameAsDelivery && (
                <div className="order-form-grid">
                  <label>Fakturační ulice<input placeholder="Ulice a číslo" value={draft.billing.street} onBlur={() => touch('billingStreet')} onChange={(event) => patchDraft({ billing: { street: event.target.value } })} /><FieldError message={errors.billingStreet} visible={shouldShowError('billingStreet')} /></label>
                  <label>Fakturační město<input placeholder="Praha" value={draft.billing.city} onBlur={() => touch('billingCity')} onChange={(event) => patchDraft({ billing: { city: event.target.value } })} /><FieldError message={errors.billingCity} visible={shouldShowError('billingCity')} /></label>
                  <label>Fakturační PSČ<input placeholder="110 00" value={draft.billing.postalCode} onBlur={() => touch('billingPostalCode')} onChange={(event) => patchDraft({ billing: { postalCode: event.target.value } })} /><FieldError message={errors.billingPostalCode} visible={shouldShowError('billingPostalCode')} /></label>
                  <label>Země<input placeholder="CZ" value={draft.billing.country} onBlur={() => touch('billingCountry')} onChange={(event) => patchDraft({ billing: { country: event.target.value } })} /><FieldError message={errors.billingCountry} visible={shouldShowError('billingCountry')} /></label>
                </div>
              )}
            </section>

            <section className="order-section">
              <h2>Souhlasy</h2>
              <div className="order-consents">
                <label className="order-approval-card"><input type="checkbox" checked={draft.consent.termsAccepted} onChange={(event) => patchDraft({ consent: { termsAccepted: event.target.checked } })} /><span><strong>Obchodní podmínky</strong><em>Souhlasím s obchodními podmínkami.</em><FieldError message={errors.termsAccepted} visible={attemptedDownload} /></span></label>
                <label className="order-approval-card"><input type="checkbox" checked={draft.consent.printApprovalAccepted} onChange={(event) => patchDraft({ consent: { printApprovalAccepted: event.target.checked } })} /><span><strong>Schválení návrhu k výrobě po finální kontrole</strong><em>Rozumím, že výroba bude možná až po produkční kontrole a pozdějším PDF/X exportu.</em><FieldError message={errors.printApprovalAccepted} visible={attemptedDownload} /></span></label>
                <label className="order-approval-card"><input type="checkbox" checked={draft.consent.localAssetNoticeAccepted} onChange={(event) => patchDraft({ consent: { localAssetNoticeAccepted: event.target.checked } })} /><span><strong>Lokální uložení fotek v prohlížeči</strong><em>Rozumím, že v této MVP verzi nejsou originální fotky odeslané na server a zůstávají pouze v tomto prohlížeči.</em><FieldError message={errors.localAssetNoticeAccepted} visible={attemptedDownload} /></span></label>
                <label className="order-approval-card"><input type="checkbox" checked={draft.consent.marketingAccepted ?? false} onChange={(event) => patchDraft({ consent: { marketingAccepted: event.target.checked } })} /><span><strong>Novinky a inspirace</strong><em>Chci dostávat novinky a inspiraci.</em></span></label>
              </div>
            </section>
          </div>

          <aside className="order-side">
            <section className="order-section order-status-box">
              <h2>Stav draftu</h2>
              <span className={draft.validationSummary?.isValid ? 'order-status-badge is-ready' : 'order-status-badge is-missing'}>{primaryStatus}</span>
              <p>{missingCount} chybějících povinných položek nebo souhlasů.</p>
              <p>Nic se zatím neodesílá.</p>
            </section>

            <section className="order-section order-price-box">
              <span>Placeholder cena</span>
              <h2>Cena</h2>
              <dl>
                <div><dt>Produkt placeholder</dt><dd>{draft.pricing.productPrice} Kč</dd></div>
                <div><dt>Doprava placeholder</dt><dd>{draft.pricing.deliveryPrice} Kč</dd></div>
                <div><dt>DPH 21 %</dt><dd>{draft.pricing.vatAmount} Kč</dd></div>
                <div><dt>Celkem vč. DPH</dt><dd>{draft.pricing.totalWithVat} Kč</dd></div>
              </dl>
              <p>Finální cena bude napojena na produkční ceník v pozdější verzi.</p>
            </section>

            <section className="order-section order-production-box">
              <h2>Produkce</h2>
              <dl>
                <div><dt>Preflight stav</dt><dd>{preflightLabels[draft.production.preflightStatus]}</dd></div>
                <div><dt>Produkční PDF/PDF-X</dt><dd>Vypnuto</dd></div>
                <div><dt>Cloud asset upload</dt><dd>Vypnuto</dd></div>
              </dl>
              {draft.production.hasErrors && <strong>Produkční export blokován</strong>}
            </section>

            <section className="order-section order-outputs">
              <h2>Výstupy</h2>
              <button type="button" onClick={downloadOrderDraft}>Stáhnout order-draft.json</button>
              <button type="button" onClick={downloadProjectJson}>Stáhnout project.json</button>
              <Button href="/preflight" variant="secondary">Zpět na preflight</Button>
              <button type="button" disabled>Odeslat objednávku</button>
              <button type="button" disabled>Zaplatit</button>
              <button type="button" disabled>Nahrát produkční assety</button>
            </section>
          </aside>
        </div>
      </div>
    </main>
  );
}
